import BarChart from "@/src/components/atom/chart/BarChart";
import Loader from "@/src/components/atom/Loader";
import Select from "@/src/components/atom/Select";
import Tag from "@/src/components/atom/Tag";
import Typo from "@/src/components/atom/Typo";
import DATE_FORMAT_STRINGS from "@/src/constant/dateFormat";
import { authApi } from "@/src/store/apis/auth";
import { useGetCommonCodeViaCodeNameQuery } from "@/src/store/apis/common";
import { useGetItemsQuery } from "@/src/store/apis/corporate/companyItem";
import { useGetExporterItemCodeStatisticInfoQuery } from "@/src/store/apis/dashboard";
import colorSet from "@/src/styles/color";
import { ConfigProvider } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { GroupType } from "types";
import { getMonthBetweenTwoDate } from "../util/calendar";
import {
  BAR_MINT_COLOR_SET,
  removeDuplicate,
  sortStackedBarChartPlugin,
} from "../util/chart";

interface BuyerVolumeStatisticProps {
  tab: "daily" | "monthly";
  period: [Dayjs, Dayjs];
  exporterItemId?: string[];
  category: string;
  onItemChange: (itemId?: string) => void;
  onRemoveItem: (value: string) => void;
  onCategoryChange: (category: string) => void;
}

const DATE_FORMAT = DATE_FORMAT_STRINGS["YY.MM.DD"];

const mainCategoryEmptyArray: any[] = [];
const emptyArray: any[] = [];

const ItemCodeVolumeStatistic = ({
  tab,
  period,
  exporterItemId,
  category,
  onItemChange,
  onRemoveItem,
  onCategoryChange,
}: BuyerVolumeStatisticProps) => {
  const { t } = useTranslation();
  const { currentData: statistics } = useGetExporterItemCodeStatisticInfoQuery(
    {
      mainCategoryCodeItemName: category === "all" ? undefined : category,
      exporterItemIds: exporterItemId?.includes("all")
        ? undefined
        : exporterItemId?.join(","),
      etdAtFrom: period[0].startOf("day").toISOString(),
      etdAtTo: period[1].endOf("day").toISOString(),
    },
    { refetchOnMountOrArgChange: true },
  );
  const { currentData, isFetching } = useGetItemsQuery(
    { mainCategoryCodeItemName: category === "all" ? undefined : category },
    { refetchOnMountOrArgChange: true },
  );

  const { mainCategoryList } = authApi.endpoints.getSession.useQueryState(
    undefined,
    {
      selectFromResult: ({ currentData, isError, isFetching }) => {
        const isErrorAndUndefined =
          isError || isFetching || currentData === undefined;

        const isManager =
          currentData?.row.exporterUserType === "MANAGER" ||
          currentData?.row.exporterUserType === "MIDDLE_MANAGER";

        return {
          mainCategoryList: !isErrorAndUndefined
            ? isManager
              ? currentData.row.mainCategoryCodeItemNames
              : currentData.row.exporter.mainCategoryCodeItemNames
            : mainCategoryEmptyArray,
        };
      },
    },
  );
  const { isMainCategoryFetching, mainCategoryCurrentData } =
    useGetCommonCodeViaCodeNameQuery(
      {
        codeName: "MAIN_CATEGORY",
      },
      {
        refetchOnMountOrArgChange: true,
        selectFromResult: ({ currentData, isError, isFetching }) => {
          const isErrorAndUndefined = isError || currentData === undefined;

          return {
            isMainCategoryFetching: isFetching,
            mainCategoryCurrentData: !isErrorAndUndefined
              ? (currentData ?? emptyArray)
              : emptyArray,
          };
        },
      },
    );

  const mainCategoryOptionList = mainCategoryCurrentData
    ?.filter((item) => mainCategoryList.includes(item.codeItemName))
    .map((item) => {
      return {
        label: item.codeItemNameEn,
        value: item.codeItemName,
      };
    });

  const itemOptionList =
    currentData?.rows
      .filter((row) =>
        mainCategoryList.includes(
          row.exporterItemListQueryResultDto.mainCategoryCodeItemName,
        ),
      )
      .reduce((acc: GroupType[], row) => {
        const groupLabel = `${row.exporterItemListQueryResultDto.mainCategoryCodeItemName} > ${row.exporterItemListQueryResultDto.subCategoryCodeItemName}`;

        // 그룹이 이미 존재하는지 확인
        let group = acc.find((g) => g.label === groupLabel);
        if (!group) {
          group = { label: groupLabel, options: [] };
          acc.push(group);
        }

        // 옵션 추가
        group.options.push({
          label: row.item,
          value: row.id.toString(),
        });

        return acc;
      }, []) || [];

  const labels =
    tab === "monthly"
      ? getMonthBetweenTwoDate({ from: period[0], to: period[1] })
      : removeDuplicate(
          statistics?.completedTaskItemCodeTransactionInfoList.map(({ etdAt }) =>
            dayjs(etdAt).format(DATE_FORMAT)
          ) ?? []
        );

  const chartData = (statistics?.exporterItemCodeRankList ?? []).map((item, idx) => {
    const data = Array(labels.length).fill(0);

    const transactions = (statistics?.completedTaskItemCodeTransactionInfoList ?? []).filter(
      (t) => t.exporterItemCodeId === item.exporterItemCodeId
    );

    transactions.forEach((t) => {
      const label =
        tab === "monthly"
          ? dayjs(t.etdAt).format("YY.MM")
          : dayjs(t.etdAt).format(DATE_FORMAT);
      const labelIdx = labels.indexOf(label);
      if (labelIdx !== -1) {
        data[labelIdx] += Number(t.netWeight);
      }
    });

    return {
      label: item.exporterItemCode,
      data,
      backgroundColor: BAR_MINT_COLOR_SET[idx % BAR_MINT_COLOR_SET.length],
      categoryPercentage: 0.5,
      barPercentage: 0.9,
      maxBarThickness: 64,
    };
  });

  const getMinIndex = () => {
    const dataPoke = chartData.map(({ data }) => data);
    const dataLengthPoke = chartData.map(({ data }) => data.length);
    const maxLength = Math.max(...dataLengthPoke);
    const datasetLength = chartData.length;

    let result = [];

    for (let i = 0; i < maxLength; i++) {
      let arr = [];
      for (let j = 0; j < datasetLength; j++) {
        arr.push({
          value: dataPoke[j][i] || Infinity,
          dataIndex: i,
          datasetIndex: j,
        });
      }
      const minArray = arr.reduce((acc, cur) => {
        if (acc.value > cur.value) {
          return cur;
        }
        return acc;
      }, arr[0]);

      result.push(minArray);
    }

    return result;
  };

  const renderDateByTab = () => {
    return (
      <Typo as="h4" typoType="h4" color="gray2">
        <>
          {period[0].format("YY.MM.DD")}~{period[1].format("YY.MM.DD")}
        </>
      </Typo>
    );
  };

  const renderChartByTab = () => {
    return (
      <>
        <GridDiv gap={exporterItemId ? 1 : undefined}>
          {[
            {
              label: t("common:totalTradingVolume"),
              value: statistics?.totalWeight ?? "-",
              isHidden: false,
            },
            {
              label: `${t("common:top1")} ${
                statistics?.firstExporterItemCode ?? "-"
              }`,
              value: statistics?.firstExporterItemCodeInfo ?? "-",
              isHidden: !!exporterItemId,
            },
            {
              label: `${t("common:top2")} ${
                statistics?.secondExporterItemCode ?? "-"
              }`,
              value: statistics?.secondExporterItemCodeInfo ?? "-",
              isHidden: !!exporterItemId,
            },
            {
              label: `${t("common:top3")} ${
                statistics?.thirdExporterItemCode ?? "-"
              }`,
              value: statistics?.thirdExporterItemCodeInfo ?? "-",
              isHidden: !!exporterItemId,
            },
          ].map(({ label, value, isHidden }) => {
            if (isHidden) return null;

            return (
              <FlexColumnCenter className="gap-8">
                <Typo typoType="b9m" color="gray6">
                  {label}
                </Typo>
                <Typo typoType="h4" color="blue2">
                  {value}
                </Typo>
              </FlexColumnCenter>
            );
          })}
        </GridDiv>
        <div>
          <BarChart
            type="bar"
            height={377}
            data={{
              datasets: chartData.map((item) => ({
                ...item,
                minIndex: getMinIndex(),
              })),
              labels: labels,
            }}
            plugins={[sortStackedBarChartPlugin]}
            options={{
              plugins: {
                legend: {
                  position: "bottom",
                  labels: {
                    color: colorSet.gray6,
                    boxWidth: 8,
                    boxHeight: 8,
                    pointStyle: "circle",
                    usePointStyle: true,
                    font: {
                      size: 13,
                    },
                  },
                },
                datalabels: {
                  font: {
                    size: 13,
                  },
                  color: colorSet.gray5,
                  formatter: (_, context) => {
                    const datasetArray: { number: number; index: number }[] =
                      [];

                    const contextDataIndex = context.dataIndex;
                    const contextDatasetIndex = context.datasetIndex;

                    context.chart.data.datasets.forEach((dataset) => {
                      if (dataset.data[context.dataIndex] !== undefined) {
                        datasetArray.push({
                          number: dataset.data[context.dataIndex] as number,
                          index: context.dataIndex,
                        });
                      }
                    });

                    const total = datasetArray.reduce(
                      (total, data) => total + data.number,
                      0,
                    );

                    if (
                      !!((context.dataset as any).minIndex as []).find(
                        ({ dataIndex, datasetIndex }) =>
                          dataIndex === contextDataIndex &&
                          datasetIndex === contextDatasetIndex,
                      )
                    ) {
                      return total === 0 ? "" : total.toLocaleString();
                    } else {
                      return "";
                    }
                  },
                  anchor: "end",
                  align: "end",
                },
                tooltip: {
                  bodyColor: colorSet.gray8,
                  titleColor: colorSet.gray10,
                  bodyFont: {
                    size: 14,
                  },
                  xAlign: "left",
                  yAlign: "center",
                  callbacks: {
                    title: (chartItem) => chartItem[0].dataset.label,
                    label: (a) => {
                      return `${a.formattedValue} MT`;
                    },
                  },
                },
              },
              layout: {
                padding: {
                  top: 23,
                },
              },
              maintainAspectRatio: false,
              scales: {
                x: {
                  stacked: true,
                },
                y: {
                  stacked: true,
                },
              },
            }}
          />
        </div>
      </>
    );
  };

  return (
    <Article>
      <StyledHeader>
        <Flex>{renderDateByTab()}</Flex>
        <Flex className="gap-8">
          <Flex className="gap-8">
            <StyledLabel>
              <Typo typoType="b10r" color="gray4">
                {t("common:category")}
              </Typo>
              <ConfigProvider
                theme={{
                  token: {
                    controlHeight: 34,
                    fontSize: 14,
                  },
                }}
              >
                <Select
                  size="middle"
                  options={[
                    { label: t("common:all"), value: "all" },
                    ...mainCategoryOptionList,
                  ]}
                  style={{ width: "240px", height: "34px" }}
                  value={category}
                  disabled={isMainCategoryFetching}
                  suffixIcon={isMainCategoryFetching ? <Loader /> : undefined}
                  onChange={(value) => {
                    onCategoryChange(value);
                  }}
                />
              </ConfigProvider>
            </StyledLabel>
          </Flex>
          <Flex className="gap-8">
            <StyledLabel>
              <Typo typoType="b10r" color="gray4">
                {t("common:itemCode")}
              </Typo>
              <ConfigProvider
                theme={{
                  token: {
                    controlHeight: 34,
                    fontSize: 14,
                  },
                }}
              >
                <Select
                  mode="multiple"
                  size="middle"
                  options={[
                    { label: t("common:all"), value: "all" },
                    ...itemOptionList,
                  ]}
                  style={{ width: "240px", height: "34px" }}
                  value={exporterItemId || "all"}
                  disabled={isFetching}
                  suffixIcon={isFetching ? <Loader /> : undefined}
                  onSelect={onItemChange}
                  onDeselect={(value) => {
                    const isOnlyOneSelected = exporterItemId?.length === 1;
                    if (isOnlyOneSelected) return;
                    onItemChange(value);
                  }}
                  maxTagCount="responsive"
                  tagRender={({ value, label }) => {
                    return (
                      <Tag
                        style={{ margin: "4px" }}
                        label={label}
                        color={"blue4"}
                        tagColor={"blue10"}
                        onRemove={() => {
                          onRemoveItem(value);
                        }}
                      />
                    );
                  }}
                />
              </ConfigProvider>
            </StyledLabel>
          </Flex>
        </Flex>
      </StyledHeader>

      <FlexColumn>{renderChartByTab()}</FlexColumn>
    </Article>
  );
};

export default ItemCodeVolumeStatistic;

const Article = styled.article`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Flex = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  .gap-8 {
    gap: 8px;
  }
`;

const StyledLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .gap-8 {
    gap: 8px;
  }
`;

const FlexColumnCenter = styled(FlexColumn)`
  align-items: center;
  text-align: center;
`;

const GridDiv = styled.div<{ gap?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ gap }) => (gap ? gap : 4)}, 1fr);
`;
